import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
} from '@mui/material';

import { useQuery } from '@tanstack/react-query';
import { useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import 'dayjs/locale/uk';
import { fetchParentStudents, fetchStudentSummary } from '../../api/students';
import { fetchLessons } from '../../api/lessons';
import { fetchPayments } from '../../api/payments';
import useAuthStore from '../../store/authStore';
import StudentInfoCard from '../../components/student/StudentInfoCard';
import PaymentHistory from '../../components/payment/PaymentHistory';
import JournalHeader from '../../components/student/JournalHeader';
import PaymentBadge from '../../components/payment/PaymentBadge';
import LessonScoreCell from '../../components/lessons/LessonScoreCell';
import LessonFormatBadge from '../../components/lessons/LessonFormatBadge';
import { useNavigate } from 'react-router-dom';
import LessonFilters from '../../components/lessons/LessonFilters';
import LessonPerformanceChart from '../../components/lessons/LessonPerformanceChart';
import LessonCardMobile from '../../components/lessons/LessonCardMobile';
import HomeworkCell from '../../components/lessons/HomeworkCell';
import ExpandableText from '../../components/common/ExpandableText';
import AssessmentTable from '../../components/lessons/AssessmentTable';
import DownloadIcon from '@mui/icons-material/Download';
import { exportLessonsToExcel } from '../../utils/exportLessonsToExcel';

dayjs.locale('uk');

const B = { primary: '#4357AD', dark: '#1E2A4A' };
const cardSx = { borderRadius: 4, boxShadow: '0 1px 6px rgba(0,0,0,0.04)', bgcolor: '#fff' };

const ParentJournalPage = () => {
  const { user } = useAuthStore();
  const { studentId } = useParams();
  const navigate = useNavigate();
  const isNarrow = useMediaQuery('(max-width:900px)');
  const [topicFilter, setTopicFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(null);

  const studentsQuery = useQuery({
    queryKey: ['parent-students', user?.id],
    queryFn: () => fetchParentStudents(user.id),
    enabled: Boolean(user?.id),
  });

  const student = useMemo(
    () => (studentsQuery.data || []).find((s) => String(s.id) === String(studentId)),
    [studentsQuery.data, studentId]
  );

  const lessonsQuery = useQuery({
    queryKey: ['parent-lessons', studentId],
    queryFn: () => fetchLessons(studentId),
    enabled: Boolean(studentId),
  });

  const summaryQuery = useQuery({
    queryKey: ['student-summary', studentId],
    queryFn: () => fetchStudentSummary(studentId),
    enabled: Boolean(studentId),
  });

  const paymentsQuery = useQuery({
    queryKey: ['payments', studentId],
    queryFn: () => fetchPayments(studentId),
    enabled: Boolean(studentId),
  });

const filteredLessons = useMemo(() => {
  const lessons = lessonsQuery.data || [];
  return lessons
    .filter((lesson) => {
      const matchesTopic = topicFilter
        ? (lesson.topic || '').toLowerCase().includes(topicFilter.toLowerCase())
        : true;
      const lessonDate = dayjs(lesson.date).format('YYYY-MM-DD');
      const matchesDateFrom = dateFrom ? lessonDate >= dateFrom : true;
      const matchesDateTo = dateTo ? lessonDate <= dateTo : true;
      return matchesTopic && matchesDateFrom && matchesDateTo;
    })
    .sort((a, b) => dayjs(b.date).valueOf() - dayjs(a.date).valueOf());
}, [lessonsQuery.data, topicFilter, dateFrom, dateTo]);

  const availableMonths = useMemo(() => {
    const months = new Map();
    (lessonsQuery.data || []).forEach((l) => {
      const d = dayjs(l.date);
      const key = d.format('YYYY-MM');
      if (!months.has(key)) {
        months.set(key, { key, label: d.format('MMM YYYY') });
      }
    });
    return [...months.values()].sort((a, b) => a.key.localeCompare(b.key));
  }, [lessonsQuery.data]);

  const displayedLessons = useMemo(() => {
    if (!selectedMonth) return filteredLessons;
    return filteredLessons.filter((l) => dayjs(l.date).format('YYYY-MM') === selectedMonth);
  }, [filteredLessons, selectedMonth]);

  if (studentsQuery.isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress sx={{ color: B.primary }} />
      </Box>
    );
  }

  if (!student) {
    return <Typography sx={{ color: '#8891A5' }}>Учня не знайдено.</Typography>;
  }

  return (
    <Stack spacing={3}>
      <JournalHeader student={student} onBack={() => navigate('/parent')} />

      <StudentInfoCard student={student} summary={summaryQuery.data} />

      <Card sx={{ ...cardSx, overflow: 'hidden', border: `2px solid ${B.primary}` }}>
        {lessonsQuery.isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress sx={{ color: B.primary }} /></Box>
        ) : (
          <>
          <Box sx={{ px: { xs: 2, md: 3 }, py: 2, bgcolor: '#F8F7FF', borderBottom: '2px solid #EDE9FE', display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, alignItems: { md: 'center' }, justifyContent: 'space-between' }}>
            <LessonFilters topicFilter={topicFilter} onTopicChange={setTopicFilter} dateFrom={dateFrom} onDateFromChange={setDateFrom} dateTo={dateTo} onDateToChange={setDateTo} />
            <Button variant="outlined" startIcon={<DownloadIcon />} disabled={!filteredLessons.length} onClick={() => exportLessonsToExcel(filteredLessons, student.name)}
              sx={{ borderColor: '#EDE9FE', color: B.primary, borderRadius: 3, textTransform: 'none', fontWeight: 600, flexShrink: 0, '&:hover': { borderColor: B.primary, bgcolor: '#EDE9FE' } }}>
              Експорт
            </Button>
          </Box>
          {availableMonths.length > 1 && (
            <Box sx={{ px: { xs: 2, md: 3 }, py: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap', borderBottom: '1px solid #EDE9FE', bgcolor: '#fff' }}>
              <Chip label="Усі" size="small" onClick={() => setSelectedMonth(null)}
                sx={{ fontWeight: 600, fontSize: 12, cursor: 'pointer', bgcolor: !selectedMonth ? B.primary : '#F3F0FF', color: !selectedMonth ? '#fff' : '#8891A5', '&:hover': { bgcolor: !selectedMonth ? '#3648A0' : '#EDE9FE' } }} />
              {availableMonths.map((m) => (
                <Chip key={m.key} label={m.label} size="small" onClick={() => setSelectedMonth(m.key)}
                  sx={{ fontWeight: 600, fontSize: 12, cursor: 'pointer', textTransform: 'capitalize', bgcolor: selectedMonth === m.key ? B.primary : '#F3F0FF', color: selectedMonth === m.key ? '#fff' : '#8891A5', '&:hover': { bgcolor: selectedMonth === m.key ? '#3648A0' : '#EDE9FE' } }} />
              ))}
            </Box>
          )}
          {isNarrow ? (
            <Box>
              {displayedLessons.map((lesson, idx) => (
                <LessonCardMobile
                  key={lesson.id}
                  lesson={lesson}
                  rowNum={displayedLessons.length - idx}
                  statusSlot={<PaymentBadge paid={lesson.paid} disabled size="md" />}
                />
              ))}
              {!displayedLessons.length && (
                <Box sx={{ py: 6, textAlign: 'center' }}>
                  <Typography variant="body2" sx={{ color: '#8891A5' }}>Немає уроків за заданими критеріями.</Typography>
                </Box>
              )}
            </Box>
          ) : (
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table sx={{ minWidth: 980 }}>
              <TableHead>
                <TableRow sx={{ bgcolor: '#F8F7FF', '& th': { color: B.primary, fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.8, textAlign: 'center', borderBottom: '2px solid #EDE9FE', borderRight: '1px solid #EDE9FE', py: 1.8, px: { xs: 1, md: 2.5 }, '&:last-child': { borderRight: 'none' } } }}>
                  <TableCell sx={{ width: 50 }}>#</TableCell>
                  <TableCell>Дата</TableCell>
                  <TableCell>Тема</TableCell>
                  <TableCell>Примітки</TableCell>
                  <TableCell>Домашнє завдання</TableCell>
                  <TableCell align="center">Оцінка</TableCell>
                  <TableCell align="center">Оплата</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayedLessons.map((lesson, idx) => (
                  <TableRow key={lesson.id} sx={{ transition: 'all 0.15s', bgcolor: idx % 2 === 0 ? '#fff' : '#FAFAFF', '&:hover': { bgcolor: '#EDE9FE' }, '& td': { py: { xs: 1, md: 2 }, px: { xs: 1, md: 2.5 }, borderBottom: '1px solid #F1F0FB', borderRight: '1px solid #F1F0FB', '&:last-child': { borderRight: 'none' } } }}>
                    <TableCell>
                      <Box sx={{ width: 32, height: 32, borderRadius: 2, bgcolor: B.primary, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 13 }}>
                        {String(displayedLessons.length - idx).padStart(2, '0')}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Stack>
                        <Typography variant="body2" fontWeight={700} sx={{ color: B.dark }}>{dayjs(lesson.date).format('DD.MM.YYYY')}</Typography>
                        <Typography variant="caption" sx={{ color: '#8891A5' }}>
                          {dayjs(lesson.date).format('dddd')}
                          {lesson.scheduleStartTime && (
                            <>, {dayjs(lesson.scheduleStartTime).format('HH:mm')}</>
                          )}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography lang="uk" variant="body2" fontWeight={700} sx={{ color: lesson.topic ? B.dark : '#8891A5', fontStyle: lesson.topic ? 'normal' : 'italic', hyphens: 'auto', overflowWrap: 'break-word', wordBreak: 'normal' }}>
                        {lesson.topic || 'Не заповнено'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 260, minWidth: 200 }}>
                      {lesson.notes ? (
                        <ExpandableText lines={4} sx={{ bgcolor: '#F8F7FF', border: '1px solid #EDE9FE', borderRadius: 2, px: 1.5, py: 0.75 }}>
                          {lesson.notes}
                        </ExpandableText>
                      ) : (
                        <Typography variant="body2" sx={{ color: '#C4B5FD', fontStyle: 'italic' }}>Без приміток</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 260, minWidth: 200 }}>
                      <HomeworkCell lesson={lesson} />
                    </TableCell>
                    <TableCell align="center"><LessonScoreCell lesson={lesson} /></TableCell>
                    <TableCell align="center"><PaymentBadge paid={lesson.paid} disabled /></TableCell>
                  </TableRow>
                ))}
                {!displayedLessons.length && (
                  <TableRow><TableCell colSpan={7} align="center" sx={{ py: 6, borderBottom: 'none' }}><Typography variant="body2" sx={{ color: '#8891A5' }}>Немає уроків за заданими критеріями.</Typography></TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          )}
          </>
        )}
      </Card>

      <AssessmentTable studentId={studentId} />

      <LessonPerformanceChart lessons={filteredLessons} />

      <PaymentHistory payments={paymentsQuery.data} isLoading={paymentsQuery.isLoading} />
    </Stack>
  );
};

export default ParentJournalPage;

